import type {ReactNode} from 'react';

import styled from '@emotion/styled';
import {useTranslation} from 'react-i18next';

import {ResponsiveImage} from 'UI/components/modules/ResponsiveImage';

import {LocationOnIcon} from 'Images/icons';

/** Diameter of the thumbnail circle in rem, when `size` is not overridden. */
const DEFAULT_SIZE = 6.4;
/** Opacity applied to the circle when `dimmed` is set (e.g. For locked waypoints). */
const DIMMED_OPACITY = 0.55;

/**
 * The props for the `WaypointThumbnail` component.
 * Configures a circular waypoint image with an overlapping number badge.
 */
interface ComponentProps {
    /** Small badge rendered overlapping the bottom-right of the circle (e.g. Lock or state icon). */
    cornerIcon?: ReactNode;
    /** Whether the thumbnail should render muted (e.g. For locked waypoints). */
    dimmed?: boolean;
    /** Alt text for the waypoint image. */
    imageAlt?: string;
    /** URL of the waypoint's main image. Falls back to a placeholder icon when omitted. */
    imageUrl?: string;
    /** The waypoint number shown in the overlapping badge. */
    number: number;
    /**
     * Diameter of the circle in rem.
     *
     * @default 6.4
     */
    size?: number;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'WaypointThumbnail'
     */
    testId?: string;
}

/**
 * A circular waypoint image with a small numbered badge overlapping the top-left edge,
 * and an optional status badge (e.g. Lock/checkmark) overlapping the bottom-right edge.
 * Falls back to a placeholder icon when no image is available..
 *
 * @param props            The component props.
 * @param props.cornerIcon Optional status badge overlapping the bottom-right edge.
 * @param props.dimmed     Whether the thumbnail should render muted.
 * @param props.imageAlt   Alt text for the waypoint image.
 * @param props.imageUrl   URL of the waypoint's main image.
 * @param props.number     The waypoint number shown in the badge.
 * @param props.size       Diameter of the circle in rem. Defaults to `6.4`.
 * @param props.testId     An optional test ID used for automation selectors. Defaults to `'WaypointThumbnail'`.
 * @returns A JSX element rendering the WaypointThumbnail.
 *
 * @example
 * ```tsx
 * <WaypointThumbnail imageUrl="/media/stop-1.jpg" number={1} />
 * <WaypointThumbnail cornerIcon={<LockIcon height={14} width={14} />} dimmed number={4} />
 * ```
 */
const WaypointThumbnail = ({
    cornerIcon,
    dimmed = false,
    imageAlt,
    imageUrl,
    number,
    size = DEFAULT_SIZE,
    testId = 'WaypointThumbnail'
}: ComponentProps) => {
    const {t} = useTranslation();

    return (
        <Wrapper $size={size} data-cy={testId}>
            <Circle $dimmed={dimmed}>
                {imageUrl
                    ? <ResponsiveImage alt={imageAlt ?? ''} src={imageUrl} />
                    : (
                        <Placeholder>
                            <LocationOnIcon height={24} width={24} />
                        </Placeholder>
                    )}
            </Circle>
            <NumberBadge aria-label={t('waypoint.categoryLabel', {number})} data-cy={`${testId}-number`}>
                <span aria-hidden="true">{number}</span>
            </NumberBadge>
            {cornerIcon && (
                <CornerBadge $dimmed={dimmed} data-cy={`${testId}-corner`}>
                    {cornerIcon}
                </CornerBadge>
            )}
        </Wrapper>
    );
};

WaypointThumbnail.displayName = 'WaypointThumbnail';

export {WaypointThumbnail};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.div<{$size: number}>`
    flex-shrink: 0;
    height: ${({$size}) => `${$size}rem`};
    position: relative;
    width: ${({$size}) => `${$size}rem`};
`;

const Circle = styled.div<{$dimmed: boolean}>`
    background: ${({theme}) => theme.colors.surfaceHigh};
    border-radius: 50%;
    height: 100%;
    opacity: ${({$dimmed}) => ($dimmed ? DIMMED_OPACITY : 1)};
    overflow: hidden;
    width: 100%;
`;

const Placeholder = styled.div`
    align-items: center;
    color: ${({theme}) => theme.colors.inkTertiary};
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
`;

const NumberBadge = styled.span`
    align-items: center;
    background: ${({theme}) => theme.colors.primaryDefault};
    border: 2px solid ${({theme}) => theme.colors.surfaceBackground};
    border-radius: 50%;
    color: ${({theme}) => theme.colors.white};
    display: flex;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    font-weight: 700;
    height: 2.2rem;
    justify-content: center;
    left: -0.4rem;
    position: absolute;
    top: -0.4rem;
    width: 2.2rem;
`;

const CornerBadge = styled.span<{$dimmed: boolean}>`
    align-items: center;
    background: ${({theme}) => theme.colors.white};
    border-radius: 50%;
    bottom: -0.2rem;
    box-shadow: ${({theme}) => theme.boxShadows.cardShadow};
    color: ${({$dimmed, theme}) => ($dimmed ? theme.colors.inkTertiary : theme.colors.primaryDefault)};
    display: flex;
    height: 2.4rem;
    justify-content: center;
    position: absolute;
    right: -0.2rem;
    width: 2.4rem;
`;